import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Select from 'react-select';
import './Factures.css';

export default function ModifierBonLivraison() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [factures, setFactures] = useState([]);
    const [filteredFactures, setFilteredFactures] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [searchProduit, setSearchProduit] = useState("");
    
    // État du formulaire
    const [bl, setBl] = useState({
        id_client: '',
        id_facture: '',
        date_livraison: '',
        adresse_livraison: '',
        destinataire: '',
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    useEffect(() => {
        fetchBonLivraison();
        fetchClients();
        fetchProduits();
        fetchFactures();
    }, [id]);

    // Filtrer les factures par client sélectionné
    useEffect(() => {
        if (bl.id_client) {
            const facturesFiltrees = factures.filter(f => f.id_client === parseInt(bl.id_client));
            setFilteredFactures(facturesFiltrees);
        } else {
            setFilteredFactures([]);
        }
    }, [bl.id_client, factures]);

    const fetchBonLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setBl(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                const produitsAvecStock = (result.data || []).map(p => ({
                    ...p,
                    stock_actuel: p.quantite_disponible || p.stock_reel || 0
                }));
                setProduits(produitsAvecStock);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const fetchFactures = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setFactures(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement factures", err);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            lot: '',
            date_peremption: ''
        };
        setBl({
            ...bl,
            lignes: [...bl.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = bl.lignes.filter((_, i) => i !== index);
        setBl({ ...bl, lignes: nouvellesLignes });
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...bl.lignes];
        nouvellesLignes[index][champ] = valeur;
        setBl({ ...bl, lignes: nouvellesLignes });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!bl.id_client) {
            setError("Veuillez sélectionner un client");
            return;
        }
        
        if (bl.lignes.length === 0) {
            setError("Veuillez ajouter au moins un produit");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(bl)
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Bon de livraison modifié avec succès !");
                setTimeout(() => {
                    navigate('/ventes/blListe');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la modification");
            }
        } catch (err) {
            console.error("Erreur:", err);
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccess("Bon de livraison supprimé avec succès !");
                setTimeout(() => {
                    navigate('/ventes/blListe');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const isEditable = () => {
        return bl.statut === 'brouillon';
    };

    // Options pour React Select
    const clientOptions = clients.map(client => ({
        value: client.id_client,
        label: `${client.raison_sociale} (${client.code_client})`,
        adresse: client.adresse,
        raison_sociale: client.raison_sociale,
        telephone: client.telephone,
        email: client.email
    }));

    const factureOptions = filteredFactures.map(facture => ({
        value: facture.id_facture,
        label: `${facture.numero_facture} - ${formatCurrency(facture.net_a_payer)}`,
        numero: facture.numero_facture
    }));

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderWidth: '2px',
            borderColor: state.isFocused ? '#21ACCD' : '#e2e8f0',
            borderRadius: '12px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(33, 172, 205, 0.1)' : 'none',
            '&:hover': {
                borderColor: '#21ACCD'
            }
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            backgroundColor: isSelected ? '#21ACCD' : isFocused ? '#e8f0fe' : 'white',
            color: isSelected ? 'white' : '#1e293b',
            '&:active': {
                backgroundColor: '#21ACCD'
            }
        }),
        placeholder: (base) => ({
            ...base,
            color: '#94a3b8'
        })
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du bon de livraison...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-pencil-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Modifier le bon de livraison</h4>
                                    <p className="text-muted small mb-0">
                                        {bl.numero_bl}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/ventes/blListe')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 text-white">
                                                <i className="ri-information-line me-2"></i>
                                                Informations générales
                                            </h5>
                                            <span className="badge bg-light text-dark px-3 py-2 rounded-pill">
                                                {bl.statut === 'brouillon' ? ' Modifiable' : 'Non modifiable'}
                                            </span>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Client <span className="text-danger">*</span></Form.Label>
                                                    <Select
                                                        options={clientOptions}
                                                        value={clientOptions.find(option => option.value === parseInt(bl.id_client))}
                                                        onChange={(selected) => {
                                                            const client = clients.find(c => c.id_client === selected?.value);
                                                            setBl({
                                                                ...bl,
                                                                id_client: selected?.value || '',
                                                                adresse_livraison: client?.adresse || '',
                                                                destinataire: client?.raison_sociale || ''
                                                            });
                                                        }}
                                                        placeholder="Rechercher un client..."
                                                        isClearable
                                                        isDisabled={!isEditable()}
                                                        styles={customSelectStyles}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        noOptionsMessage={() => "Aucun client trouvé"}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Facture associée</Form.Label>
                                                    <Select
                                                        options={factureOptions}
                                                        value={factureOptions.find(option => option.value === parseInt(bl.id_facture))}
                                                        onChange={(selected) => {
                                                            setBl({...bl, id_facture: selected?.value || ''});
                                                        }}
                                                        placeholder={bl.id_client ? "Rechercher une facture..." : "Sélectionnez d'abord un client"}
                                                        isClearable
                                                        isDisabled={!isEditable() || !bl.id_client}
                                                        styles={customSelectStyles}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        noOptionsMessage={() => bl.id_client ? "Aucune facture pour ce client" : "Sélectionnez d'abord un client"}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date livraison <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={bl.date_livraison}
                                                        onChange={(e) => setBl({...bl, date_livraison: e.target.value})}
                                                        required
                                                        disabled={!isEditable()}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Destinataire</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={bl.destinataire || ''}
                                                        onChange={(e) => setBl({...bl, destinataire: e.target.value})}
                                                        placeholder="Nom du destinataire"
                                                        disabled={!isEditable()}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Adresse de livraison</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={bl.adresse_livraison || ''}
                                                        onChange={(e) => setBl({...bl, adresse_livraison: e.target.value})}
                                                        placeholder="Adresse complète de livraison"
                                                        disabled={!isEditable()}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={bl.notes || ''}
                                                        onChange={(e) => setBl({...bl, notes: e.target.value})}
                                                        placeholder="Instructions particulières..."
                                                        disabled={!isEditable()}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes du BL */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="py-3 d-flex justify-content-between align-items-center" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits à livrer
                                        </h5>
                                        {isEditable() && (
                                            <Button 
                                                variant="light" 
                                                size="sm"
                                                onClick={() => setShowProduitModal(true)}
                                                className="rounded-pill"
                                            >
                                                <i className="ri-add-line me-1"></i>
                                                Ajouter un produit
                                            </Button>
                                        )}
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {bl.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 products-table">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="ps-4">Référence</th>
                                                            <th style={{ width: '35%' }}>Désignation</th>
                                                            <th className="text-center">Quantité</th>
                                                            <th>Lot</th>
                                                            <th>Date péremption</th>
                                                            {isEditable() && <th className="text-center pe-4"></th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {bl.lignes.map((ligne, index) => (
                                                            <tr key={index} className="product-row">
                                                                <td className="ps-4">
                                                                    <span className="reference-badge">
                                                                        <i className="ri-barcode-line me-1 fs-10"></i>
                                                                        {ligne.reference}
                                                                    </span>
                                                                 </td>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td className="text-center" style={{ width: '100px' }}>
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        disabled={!isEditable()}
                                                                        className="text-center"
                                                                    />
                                                                 </td>
                                                                <td style={{ width: '120px' }}>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={ligne.lot || ''}
                                                                        onChange={(e) => updateLigne(index, 'lot', e.target.value)}
                                                                        placeholder="N° lot"
                                                                        size="sm"
                                                                        disabled={!isEditable()}
                                                                    />
                                                                 </td>
                                                                <td style={{ width: '130px' }}>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={ligne.date_peremption || ''}
                                                                        onChange={(e) => updateLigne(index, 'date_peremption', e.target.value)}
                                                                        size="sm"
                                                                        disabled={!isEditable()}
                                                                    />
                                                                 </td>
                                                                {isEditable() && (
                                                                    <td className="text-center pe-4">
                                                                        <Button 
                                                                            variant="link" 
                                                                            className="text-danger p-0"
                                                                            onClick={() => supprimerLigne(index)}
                                                                        >
                                                                            <i className="ri-delete-bin-line fs-16"></i>
                                                                        </Button>
                                                                     </td>
                                                                )}
                                                             </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3 d-block"></i>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                                {isEditable() && (
                                                    <Button 
                                                        variant="outline-primary"
                                                        onClick={() => setShowProduitModal(true)}
                                                        className="rounded-pill px-4"
                                                        style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                                    >
                                                        <i className="ri-add-line me-2"></i>
                                                        Ajouter un produit
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-item d-flex justify-content-between mb-3 pb-2 border-bottom">
                                            <span className="text-muted">Total articles</span>
                                            <span className="fw-bold fs-4">{bl.lignes.length}</span>
                                        </div>
                                        <div className="recap-item d-flex justify-content-between mb-4 pb-2 border-bottom">
                                            <span className="text-muted">Quantité totale</span>
                                            <span className="fw-bold fs-4">
                                                {bl.lignes.reduce((acc, l) => acc + (l.quantite || 0), 0)}
                                            </span>
                                        </div>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Statut</Form.Label>
                                            <Form.Select
                                                value={bl.statut}
                                                onChange={(e) => setBl({...bl, statut: e.target.value})}
                                                disabled={!isEditable()}
                                                className="border-2 rounded-3"
                                            >
                                                <option value="brouillon">Brouillon</option>
                                                <option value="prepare">Préparé</option>
                                            </Form.Select>
                                            {!isEditable() && (
                                                <Form.Text className="text-muted small">
                                                    Le statut ne peut plus être modifié car le bon n'est plus en brouillon
                                                </Form.Text>
                                            )}
                                        </Form.Group>

                                        <hr />

                                        <div className="d-grid gap-2">
                                           
                                                <>
                                                    <Button 
                                                        type="submit" 
                                                        variant="primary" 
                                                        size="lg"
                                                        disabled={saving}
                                                        className="rounded-pill py-2"
                                                        style={{ background: '#21ACCD', border: 'none' }}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Mise à jour...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-save-line me-2"></i>
                                                                Mettre à jour
                                                            </>
                                                        )}
                                                    </Button>
                                                    <Button 
                                                        variant="danger" 
                                                        size="lg"
                                                        onClick={() => setShowDeleteModal(true)}
                                                        className="rounded-pill py-2"
                                                    >
                                                        <i className="ri-delete-bin-line me-2"></i>
                                                        Supprimer
                                                    </Button>
                                                </>
                                        
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/blListe')}
                                                className="rounded-pill py-2"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="product-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4" style={{ background: '#21ACCD' }}>
                    <Modal.Title className="text-white">
                        <i className="ri-shopping-cart-line me-2"></i>
                        Sélectionner un produit
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    <InputGroup className="mb-3">
                        <InputGroup.Text className="bg-light rounded-pill">
                            <i className="ri-search-line"></i>
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher un produit..."
                            className="rounded-pill"
                            value={searchProduit}
                            onChange={(e) => setSearchProduit(e.target.value)}
                        />
                    </InputGroup>
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produits
                                    .filter(p => 
                                        p.reference?.toLowerCase().includes(searchProduit.toLowerCase()) ||
                                        p.designation?.toLowerCase().includes(searchProduit.toLowerCase())
                                    )
                                    .map(produit => (
                                    <tr key={produit.id_produit}>
                                        <td className="ps-4"><span className="reference-badge">{produit.reference}</span></td>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">
                                            <span className={produit.stock_actuel < 1 ? 'text-danger fw-bold' : 'text-muted'}>
                                                {produit.stock_actuel || 0}
                                            </span>
                                         </td>
                                        <td className="pe-4">
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => ajouterLigne(produit)}
                                                disabled={produit.stock_actuel < 1}
                                                className="rounded-circle"
                                                style={{ background: '#21ACCD', border: 'none', width: '32px', height: '32px' }}
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                         </td>
                                     </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button variant="outline-secondary" onClick={() => setShowProduitModal(false)} className="rounded-pill px-4">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmation de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-delete-bin-line text-danger" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Confirmer la suppression</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-2">Êtes-vous sûr de vouloir supprimer ce bon de livraison ?</p>
                    <p className="fw-bold text-primary">{bl.numero_bl}</p>
                    <Alert variant="warning" className="mt-3">
                        <i className="ri-alert-line me-2"></i>
                        Cette action est irréversible.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="px-4 rounded-pill">
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting} className="px-5 rounded-pill">
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Confirmer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                
                /* React Select Styles */
                .react-select-container {
                    width: 100%;
                }
                .react-select__control {
                    min-height: 38px;
                }
                .react-select__placeholder {
                    color: #94a3b8;
                }
                .react-select__indicator-separator {
                    display: none;
                }
                .react-select__clear-indicator {
                    color: #94a3b8;
                    cursor: pointer;
                }
                .react-select__clear-indicator:hover {
                    color: #ef4444;
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .product-modal .modal-content {
                    border-radius: 24px;
                    overflow: hidden;
                }
                
                .products-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .products-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .product-row {
                    transition: all 0.2s ease;
                }
                
                .product-row:hover {
                    background: #f8fafc;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-16 {
                    font-size: 1rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}